import React, { useState } from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import SimpleReactLightbox, { SRLWrapper } from "simple-react-lightbox";
import NavigationBtns from "./Navigation-btns";

const MandatorySetupWeb = () => {
  const [text, setText] = useState(null);

  const copyToClipBoard = async (copyMe, id) => {
    try {
      await navigator.clipboard.writeText(copyMe);
      setText(id);
    } catch (err) {
      setText("Failed to copy!");
    }
  };
  return (
    <div
      className="introduction "
      data-aos="fade-right"
      data-aos-delay="300"
      data-aos-duration="1000"
    >
      <h1 className="title">Mandatory setup &mdash; Frontend website</h1>
      <br />
      <h3 className="introduction-contentTitle"> Environment configuration </h3>
      • Open <code>/.env</code> and change every single credential with your own
      <br />• If there is no <code>.env</code> file, please create one in the
      root folder and fill it the same as in the example below
      <br />
      <div className="alert alert-primary mt-3" role="alert">
        <div className="mb-2">
          <AiOutlineInfoCircle size={22} /> <strong>INFO</strong>
        </div>
        All variables prefixed with <code>NEXT_PUBLIC_</code> are inlined into
        the browser bundle at build time &mdash; never put server-only secrets
        there. If you change any <code>NEXT_PUBLIC_*</code> value, you must
        re-run <code>npm run build</code> for it to take effect.
      </div>
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a
              href="/assets/img/doc/quick-env.png"
              data-fancybox
              rel="nofollow"
            >
              <img
                src="/assets/img/doc/quick-env.png"
                alt="quick .env example"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <h4 className="mt-3">Required</h4>
      <strong>NEXT_PUBLIC_BASE_URL</strong> &mdash; backend API base URL. Must
      end with a trailing slash, e.g.{" "}
      <code>https://demo-api-quick.githubit.com/api/</code>
      <br />
      <strong>NEXT_PUBLIC_SITE_URL</strong> &mdash; canonical public URL of this
      website, e.g. <code>https://quick-web.githubit.com</code>. Used for SEO
      and metadata.
      <br />
      <br />
      <h4>Caching &amp; revalidation</h4>
      <strong>GLOBAL_CACHE_REVALIDATE_SECONDS</strong> &mdash; TTL (in seconds)
      for the global fetch cache (languages, translations, settings,
      currencies). Default <code>60</code>. Set to <code>0</code> to disable
      time-based revalidation.
      <br />
      <br />
      <h4>Feature flags</h4>
      <strong>NEXT_PUBLIC_CUSTOM_PHONE_REGISTRATION</strong> &mdash; set to{" "}
      <code>true</code> to use a custom phone-auth provider (e.g. Twilio) for
      phone registration instead of Firebase Phone Auth. Default{" "}
      <code>false</code>.
      <br />
      <br />
      <h4>Firebase (push notifications &amp; Firestore chat)</h4>
      Firebase powers both Cloud Messaging (push notifications) and the
      Firestore-based in-app chat. Pull these values from the Firebase console
      &rarr; Project settings &rarr; General / Cloud Messaging. All keys are
      required for both features to work.
      <br />
      <br />
      NEXT_PUBLIC_FIREBASE_API_KEY=
      <br />
      NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN=
      <br />
      NEXT_PUBLIC_FIREBASE_PROJECT_ID=
      <br />
      NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET=
      <br />
      NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID=
      <br />
      NEXT_PUBLIC_FIREBASE_APP_ID=
      <br />
      NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID=
      <br />
      NEXT_PUBLIC_VAPID_KEY=
      <br />
      <br />
      <h4>How to get the VAPID key</h4>
      The VAPID key (Web Push certificate) is what the browser uses to
      authenticate with FCM when subscribing for push. It lives in a different
      tab from the other Firebase config values, so it&apos;s easy to miss.
      Steps:
      <br />
      <br />
      1. Open the{" "}
      <a
        href="https://console.firebase.google.com/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Firebase Console
      </a>{" "}
      and select your project.
      <br />
      2. Click the gear icon next to <strong>Project Overview</strong> and open{" "}
      <strong>Project settings</strong>.
      <br />
      3. Switch to the <strong>Cloud Messaging</strong> tab.
      <br />
      4. Scroll down to the <strong>Web configuration</strong> section &rarr;{" "}
      <strong>Web Push certificates</strong>.
      <br />
      5. If no key pair exists yet, click <strong>Generate key pair</strong>.
      Otherwise copy the existing <strong>Key pair</strong> value (a long Base64
      string starting with <code>B...</code>).
      <br />
      6. Paste that value into <code>NEXT_PUBLIC_VAPID_KEY</code> in your{" "}
      <code>.env</code>.
      <br />
      <br />
      <h4>Firebase service worker configuration</h4>
      The Firebase messaging service worker at{" "}
      <code>public/firebase-messaging-sw.js</code> runs <strong>outside</strong>{" "}
      of the Next.js bundle, so it cannot read your <code>.env</code>. The
      same Firebase config values must therefore be set directly inside this
      file, otherwise background push notifications will silently fail.
      <br />
      <br />
      Open <code>public/firebase-messaging-sw.js</code> and populate the{" "}
      <code>firebaseConfig</code> object with the same values you placed in
      your <code>.env</code>, as shown below:
      <div className="introduction-img-container">
        <SimpleReactLightbox>
          <SRLWrapper>
            <a
              href="/assets/img/doc/quick-firebase-sw.png"
              data-fancybox
              rel="nofollow"
            >
              <img
                src="/assets/img/doc/quick-firebase-sw.png"
                alt="firebase-messaging-sw.js example"
                loading="lazy"
                className="img-responsive-full"
              />
            </a>
          </SRLWrapper>
        </SimpleReactLightbox>
      </div>
      <div className="alert alert-primary mt-3" role="alert">
        <div className="mb-2">
          <AiOutlineInfoCircle size={22} /> <strong>INFO</strong>
        </div>
        The VAPID key does <strong>not</strong> go into{" "}
        <code>firebase-messaging-sw.js</code> &mdash; only the seven standard
        Firebase config fields shown above. The VAPID key stays in{" "}
        <code>.env</code> as <code>NEXT_PUBLIC_VAPID_KEY</code>.
      </div>
      <br />
      <h3 className="introduction-contentTitle"> Install &amp; build </h3>
      For building web data for deployment, you have to run commands:
      <br />
      • Install required packages <br />
      <div className="introduction-code">
        <div>
          <span className="ps-0">npm install</span>
          <span
            className={text === 1 ? "bg-success copy" : "copy"}
            onClick={() => copyToClipBoard("npm install", 1)}
          >
            {text === 1 ? "copied!" : "copy"}
          </span>
        </div>
      </div>
      <p className="mt-2">or</p>
      <div className="introduction-code">
        <div>
          <span className="ps-0">yarn</span>
          <span
            className={text === 2 ? "bg-success copy" : "copy"}
            onClick={() => copyToClipBoard("yarn", 2)}
          >
            {text === 2 ? "copied!" : "copy"}
          </span>
        </div>
      </div>
      <br />
      • Build frontend using the following command <br />
      <div className="introduction-code">
        <div>
          <span className="ps-0">npm run build</span>
          <span
            className={text === 3 ? "bg-success copy" : "copy"}
            onClick={() => copyToClipBoard("npm run build", 3)}
          >
            {text === 3 ? "copied!" : "copy"}
          </span>
        </div>
      </div>
      <p className="mt-2">or</p>
      <div className="introduction-code">
        <div>
          <span className="ps-0">yarn build</span>
          <span
            className={text === 4 ? "bg-success copy" : "copy"}
            onClick={() => copyToClipBoard("yarn build", 4)}
          >
            {text === 4 ? "copied!" : "copy"}
          </span>
        </div>
      </div>
      <br />
      • Run the project with PM2 <br />
      <div className="introduction-code">
        <div>
          <span className="ps-0">pm2 start "npm run start"</span>
          <span
            className={text === 5 ? "bg-success copy" : "copy"}
            onClick={() => copyToClipBoard('pm2 start "npm run start"', 5)}
          >
            {text === 5 ? "copied!" : "copy"}
          </span>
        </div>
      </div>
      <br />• Add image domains. Open <code>next.config.ts</code> and add any
      host you load images from to <code>images.remotePatterns</code> so{" "}
      <code>next/image</code> can optimize them.
      <br />
      <img src="../assets/img/next_config.jpeg" alt="" loading="lazy" />
      <img src="../assets/img/next_img_config.png" alt="" loading="lazy" />
      <div className="alert alert-primary mt-3" role="alert">
        <div className="mb-2">
          <AiOutlineInfoCircle size={22} /> <strong>INFO</strong>
        </div>
        You have to configure your server for the front website. The front
        website runs on port 3000. Open your server configuration file and add{" "}
        <div className="introduction-code">
          <div>
            <span className="ps-0">
              ProxyPass / http://localhost:3000 <br /> ProxyPassReverse /
              http://localhost:3000
            </span>
            <span
              className={text === 6 ? "bg-success copy" : "copy"}
              onClick={() =>
                copyToClipBoard(
                  "ProxyPass / http://localhost:3000  ProxyPassReverse /http://localhost:3000",
                  6
                )
              }
            >
              {text === 6 ? "copied!" : "copy"}
            </span>
          </div>
        </div>{" "}
        in your domain configuration section. Rewrite mode should be enabled on
        your server. After adding, restart your server.
      </div>
      <h4>Or</h4>
      <div className="alert alert-primary mt-3" role="alert">
        <div className="mb-2">
          <AiOutlineInfoCircle size={22} /> <strong>INFO</strong>
        </div>
        Create a <code>.htaccess</code> file in the main website directory and
        put the following content
        <div className="introduction-code">
          <div>
            <span className="ps-0">
              RewriteEngine on RewriteRule (.*) http://localhost:3000/$1
              [P,L,R=301]
            </span>
            <span
              className={text === 7 ? "bg-success copy" : "copy"}
              onClick={() =>
                copyToClipBoard(
                  "RewriteEngine on RewriteRule (.*) http://localhost:3000/$1[P,L,R=301]",
                  7
                )
              }
            >
              {text === 7 ? "copied!" : "copy"}
            </span>
          </div>
        </div>
      </div>
      <div className="iframe-wrapper mb-2">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/Fd1TXISmw-o"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
      <NavigationBtns />
    </div>
  );
};

export default MandatorySetupWeb;
